import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { validateChecklist } from '../../utils/validation';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Chip,
  Avatar,
  Divider,
  Slide,
  LinearProgress,
  IconButton
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  CheckCircle as CheckIcon,
  DirectionsCar as CarIcon,
  Assignment as FormIcon,
  Build as MaintenanceIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import '../../styles/mobile-first.css';

const initialFormData = {
  vehicle_number: '',
  driver: '',
  date: '',
  current_odometer: '',
  previous_odometer: '',
  miles_traveled: '',
  license_plate_number: '',
  oil_level: false,
  oil_level_notes: '',
  oil_change_last_date: '',
  oil_change_next_date: '',
  antifreeze_level: false,
  windshield_fluid: false,
  transmission_fluid: false,
  tire_pressure_lf: '',
  tire_pressure_rf: '',
  tire_pressure_lr: '',
  tire_pressure_rr: '',
  brake_fluid: false,
  tire_tread: false,
  fan_belt: false,
  headlights: false,
  taillights: false,
  brake_lights: false,
  running_lights: false,
  turn_signals: false,
  emergency_flashers: false,
  registration: false,
  registration_expiry: '',
  insurance: false,
  fire_extinguisher: false,
  fire_extinguisher_charged: false,
  fire_extinguisher_cert_date: '',
  safety_kit_flags: false,
  safety_kit_first_aid: false,
  safety_kit_eye_wash: false,
  safety_kit_eye_wash_expiry: '',
  safety_kit_flares: false,
  safety_kit_gloves: false,
  safety_kit_hard_hat: false,
  safety_kit_goggles: false,
  safety_kit_caution_tape: false,
  safety_kit_cones: false,
  safety_kit_harness: false,
  safety_kit_lumbar_support: false,
  tire_rotation_date: '',
  engine_tuneup_date: '',
  transmission_service_date: '',
  appearance: '',
  exterior_condition: false,
  interior_vacuumed: false,
  interior_condition: false,
  sanitizing_supplies: false,
  extension_cords: false,
  tire_traction_devices: false,
  notes: '',
  tech_signature: ''
};

const steps = [
  {
    label: 'Vehicle Info',
    icon: <CarIcon />,
    fields: ['vehicle_number', 'driver', 'date', 'license_plate_number', 'current_odometer', 'previous_odometer']
  },
  {
    label: 'Fluids & Systems',
    icon: <FormIcon />,
    fields: ['oil_level', 'oil_change_last_date', 'antifreeze_level', 'windshield_fluid', 'transmission_fluid', 'brake_fluid']
  },
  {
    label: 'Tires & Lights',
    icon: <CheckIcon />,
    fields: ['tire_pressure_lf', 'tire_pressure_rf', 'tire_pressure_lr', 'tire_pressure_rr', 'tire_tread', 'headlights', 'taillights', 'brake_lights', 'running_lights', 'turn_signals', 'emergency_flashers']
  },
  {
    label: 'Safety & Documentation',
    icon: <MaintenanceIcon />,
    fields: ['registration', 'registration_expiry', 'insurance', 'fire_extinguisher', 'fire_extinguisher_charged', 'fire_extinguisher_cert_date']
  },
  {
    label: 'Safety Kit',
    icon: <CheckIcon />,
    fields: ['safety_kit_flags', 'safety_kit_first_aid', 'safety_kit_eye_wash', 'safety_kit_eye_wash_expiry', 'safety_kit_flares', 'safety_kit_gloves', 'safety_kit_hard_hat', 'safety_kit_goggles', 'safety_kit_caution_tape', 'safety_kit_cones', 'safety_kit_harness', 'safety_kit_lumbar_support']
  },
  {
    label: 'Maintenance & Condition',
    icon: <MaintenanceIcon />,
    fields: ['tire_rotation_date', 'engine_tuneup_date', 'transmission_service_date', 'appearance', 'exterior_condition', 'interior_vacuumed', 'interior_condition', 'sanitizing_supplies', 'extension_cords', 'tire_traction_devices']
  },
  {
    label: 'Final Notes',
    icon: <FormIcon />,
    fields: ['notes', 'tech_signature']
  }
];

const MobileChecklistForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [hasLatestChecklist, setHasLatestChecklist] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLatestChecklist = async () => {
      try {
        const dashboardData = await apiService.getUserDashboard();
        if (dashboardData.latestChecklist) {
          setHasLatestChecklist(true);
          setFormData(prevState => ({
            ...prevState,
            previous_odometer: dashboardData.latestChecklist.current_odometer.toString(),
            // Auto-fill commonly unchanged fields from latest checklist
            license_plate_number: dashboardData.latestChecklist.license_plate_number || '',
            registration_expiry: dashboardData.latestChecklist.registration_expiry || '',
            fire_extinguisher_cert_date: dashboardData.latestChecklist.fire_extinguisher_cert_date || '',
            safety_kit_eye_wash_expiry: dashboardData.latestChecklist.safety_kit_eye_wash_expiry || '',
            oil_change_last_date: dashboardData.latestChecklist.oil_change_last_date || ''
          }));
        } else {
          setHasLatestChecklist(false);
        }
      } catch (error) {
        console.error('Error fetching latest checklist:', error);
        setHasLatestChecklist(false);
      }
    };

    if (user) {
      setFormData(prevState => ({
        ...prevState,
        vehicle_number: user.vehicle_number || '',
        driver: user.username || '',
        date: new Date().toISOString().split('T')[0]
      }));
      fetchLatestChecklist();
    }
  }, [user]);

  useEffect(() => {
    const current = parseInt(formData.current_odometer) || 0;
    const previous = parseInt(formData.previous_odometer) || 0;
    const milesTraveled = Math.max(current - previous, 0);
    setFormData(prevState => ({
      ...prevState,
      miles_traveled: milesTraveled.toString()
    }));
  }, [formData.current_odometer, formData.previous_odometer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const calculateProgress = () => {
    const totalFields = Object.keys(initialFormData).length;
    const filledFields = Object.entries(formData).filter(([key, value]) => {
      if (typeof value === 'boolean') return true; // Checkboxes are considered filled
      return value !== '' && value !== null && value !== undefined;
    }).length;
    
    return Math.round((filledFields / totalFields) * 100);
  };

  const validateCurrentStep = () => {
    const currentStepFields = steps[activeStep].fields;
    const stepErrors = {};
    
    currentStepFields.forEach(fieldName => {
      if (fieldName === 'vehicle_number' && !formData[fieldName]) {
        stepErrors[fieldName] = 'Unit number is required';
      }
      if (fieldName === 'driver' && !formData[fieldName]) {
        stepErrors[fieldName] = 'Driver name is required';
      }
      if (fieldName === 'date' && !formData[fieldName]) {
        stepErrors[fieldName] = 'Date is required';
      }
      if (fieldName === 'current_odometer' && !formData[fieldName]) {
        stepErrors[fieldName] = 'Current odometer is required';
      }
    });
    
    return stepErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateChecklist(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        let nextOilChangeDate = '';
        if (formData.oil_change_last_date) {
          const lastOilChangeDate = new Date(formData.oil_change_last_date);
          const milesTraveled = parseInt(formData.miles_traveled) || 0;
          if (milesTraveled > 0) {
            const currentDate = new Date(formData.date || new Date());
            const daysSinceLastOilChange = Math.max(1, Math.floor((currentDate - lastOilChangeDate) / (1000 * 60 * 60 * 24)));
            const milesPerDay = milesTraveled / daysSinceLastOilChange;
            
            if (milesPerDay > 0) {
              const daysUntilNextOilChange = Math.floor(5000 / milesPerDay);
              const nextDate = new Date(currentDate);
              nextDate.setDate(nextDate.getDate() + daysUntilNextOilChange);
              nextOilChangeDate = nextDate.toISOString().split('T')[0];
            }
          }
        }

        const submitData = {
          ...formData,
          user_id: user.id,
          miles_traveled: parseInt(formData.miles_traveled),
          oil_change_next_date: nextOilChangeDate || formData.oil_change_next_date
        };
        
        await apiService.createChecklist(submitData);
        setSubmitMessage({ type: 'success', text: 'Checklist submitted successfully!' });
        
        setTimeout(() => {
          navigate('/');
        }, 2000);

      } catch (error) {
        setSubmitMessage({ 
          type: 'error', 
          text: `Error submitting checklist: ${error.response?.data?.message || error.message}` 
        });
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setSubmitMessage({ type: 'error', text: 'Please correct the errors in the form.' });
    }
  };

  const renderTextField = (name, label, type = "text", multiline = false) => (
    <TextField
      fullWidth
      type={type}
      name={name}
      label={label}
      value={formData[name]}
      onChange={handleChange}
      error={!!errors[name]}
      helperText={errors[name]}
      variant="outlined"
      margin="normal"
      className="mobile-input"
      multiline={multiline}
      rows={multiline ? 3 : 1}
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: 48,
          borderRadius: '12px'
        }
      }}
    />
  );

  const renderAutoFillTextField = (name, label, type = "text", multiline = false) => {
    const isAutoFilled = hasLatestChecklist && formData[name];
    return (
      <TextField
        fullWidth
        type={type}
        name={name}
        label={label}
        value={formData[name]}
        onChange={handleChange}
        error={!!errors[name]}
        helperText={errors[name] || (isAutoFilled ? "Auto-filled from last checklist (editable)" : "")}
        variant="outlined"
        margin="normal"
        className="mobile-input"
        multiline={multiline}
        rows={multiline ? 3 : 1}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 48,
            borderRadius: '12px',
            backgroundColor: isAutoFilled ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
          }
        }}
      />
    );
  };

  const renderCheckbox = (name, label) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={formData[name]}
          onChange={handleChange}
          name={name}
          sx={{ minWidth: 44, minHeight: 44 }}
        />
      }
      label={label}
      className="touch-target"
      sx={{ 
        width: '100%',
        margin: '8px 0',
        '& .MuiFormControlLabel-label': {
          fontSize: '16px'
        }
      }}
    />
  );

  const renderStepContent = (stepIndex) => {
    const step = steps[stepIndex];
    
    switch (stepIndex) {
      case 0: // Vehicle Info
        return (
          <Box className="mobile-form-section">
           {renderTextField("vehicle_number", "Unit Number")}
            {renderTextField("driver", "Driver")}
            {renderTextField("date", "Date", "date")}
                            {renderAutoFillTextField("license_plate_number", "License Plate Number")}
            {renderTextField("current_odometer", "Current Odometer", "number")}
            <TextField
              fullWidth
              type="number"
              name="previous_odometer"
              label="Previous Odometer"
              value={formData.previous_odometer}
              onChange={handleChange}
              error={!!errors.previous_odometer}
              helperText={hasLatestChecklist ? 
                errors.previous_odometer : 
                (errors.previous_odometer || "Enter the previous odometer reading for your first checklist")}
              variant="outlined"
              margin="normal"
              className="mobile-input"
              InputProps={{
                readOnly: hasLatestChecklist,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px'
                }
              }}
            />
            <TextField
              fullWidth
              type="number"
              name="miles_traveled"
              label="Miles Traveled"
              value={formData.miles_traveled}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              margin="normal"
              className="mobile-input"
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px',
                  bgcolor: 'grey.100'
                }
              }}
            />
          </Box>
        );

      case 1: // Fluids & Systems
        return (
          <Box className="mobile-form-section">
            {renderCheckbox("oil_level", "Oil Level OK")}
            {formData.oil_level && renderTextField("oil_level_notes", "Oil Level Notes")}
                            {renderAutoFillTextField("oil_change_last_date", "Last Oil Change Date", "date")}
            {renderCheckbox("antifreeze_level", "Antifreeze Level OK")}
            {renderCheckbox("windshield_fluid", "Windshield Washer Fluid OK")}
            {renderCheckbox("transmission_fluid", "Transmission Fluid OK")}
            {renderCheckbox("brake_fluid", "Brake Fluid OK")}
          </Box>
        );

      case 2: // Tires & Lights
        return (
          <Box className="mobile-form-section">
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Tire Pressure
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              {renderTextField("tire_pressure_lf", "LF Pressure", "number")}
              {renderTextField("tire_pressure_rf", "RF Pressure", "number")}
              {renderTextField("tire_pressure_lr", "LR Pressure", "number")}
              {renderTextField("tire_pressure_rr", "RR Pressure", "number")}
            </Box>
            {renderCheckbox("tire_tread", "Tire Tread Adequate")}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Lights
            </Typography>
            {renderCheckbox("headlights", "Headlights")}
            {renderCheckbox("taillights", "Taillights")}
            {renderCheckbox("brake_lights", "Brake Lights")}
            {renderCheckbox("running_lights", "Running Lights")}
            {renderCheckbox("turn_signals", "Turn Signals")}
            {renderCheckbox("emergency_flashers", "Emergency Flashers")}
          </Box>
        );

      case 3: // Safety & Documentation
        return (
          <Box className="mobile-form-section">
            {renderCheckbox("registration", "Registration")}
                            {renderAutoFillTextField("registration_expiry", "Registration Expiry Date", "date")}
            {renderCheckbox("insurance", "Insurance")}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Fire Extinguisher
            </Typography>
            {renderCheckbox("fire_extinguisher", "Fire Extinguisher Present")}
            {renderCheckbox("fire_extinguisher_charged", "Fire Extinguisher Charged")}
                            {renderAutoFillTextField("fire_extinguisher_cert_date", "Fire Extinguisher Cert Date", "date")}
          </Box>
        );

      case 4: // Safety Kit
        return (
          <Box className="mobile-form-section">
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Safety Kit Items
            </Typography>
            {renderCheckbox("safety_kit_flags", "Flags")}
            {renderCheckbox("safety_kit_first_aid", "First Aid Kit")}
            {renderCheckbox("safety_kit_eye_wash", "Eye Wash")}
                            {formData.safety_kit_eye_wash && renderAutoFillTextField("safety_kit_eye_wash_expiry", "Eye Wash Expiry Date", "date")}
            {renderCheckbox("safety_kit_flares", "Flares")}
            {renderCheckbox("safety_kit_gloves", "Gloves")}
            {renderCheckbox("safety_kit_hard_hat", "Hard Hat")}
            {renderCheckbox("safety_kit_goggles", "Safety Goggles")}
            {renderCheckbox("safety_kit_caution_tape", "Caution Tape")}
            {renderCheckbox("safety_kit_cones", "Cones")}
            {renderCheckbox("safety_kit_harness", "Harness")}
            {renderCheckbox("safety_kit_lumbar_support", "Lumbar Support")}
          </Box>
        );

      case 5: // Maintenance & Condition
        return (
          <Box className="mobile-form-section">
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Maintenance Dates
            </Typography>
            {renderTextField("tire_rotation_date", "Last Tire Rotation Date", "date")}
            {renderTextField("engine_tuneup_date", "Last Engine Tuneup Date", "date")}
            {renderTextField("transmission_service_date", "Last Transmission Service Date", "date")}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" className="mobile-subtitle" gutterBottom>
              Vehicle Condition
            </Typography>
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="appearance-label">Appearance</InputLabel>
              <Select
                labelId="appearance-label"
                id="appearance"
                name="appearance"
                value={formData.appearance}
                onChange={handleChange}
                label="Appearance"
                error={!!errors.appearance}
                sx={{
                  minHeight: 48,
                  borderRadius: '12px'
                }}
              >
                <MenuItem value="clean">Clean</MenuItem>
                <MenuItem value="dirty">Dirty</MenuItem>
              </Select>
            </FormControl>
            {renderCheckbox("exterior_condition", "Exterior Condition Acceptable")}
            {renderCheckbox("interior_vacuumed", "Interior Vacuumed")}
            {renderCheckbox("interior_condition", "Interior Condition Acceptable")}
            {renderCheckbox("sanitizing_supplies", "Sanitizing Supplies")}
            {renderCheckbox("extension_cords", "Extension Cords")}
            {renderCheckbox("tire_traction_devices", "Tire Traction Devices")}
          </Box>
        );

      case 6: // Final Notes
        return (
          <Box className="mobile-form-section">
            {renderTextField("notes", "Additional Notes", "text", true)}
            {renderTextField("tech_signature", "Technician Signature")}
            
            <Card sx={{ mt: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ready to Submit
                </Typography>
                <Typography variant="body2">
                  Please review your checklist and submit when complete.
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateProgress()} 
                  sx={{ 
                    mt: 2, 
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white'
                    }
                  }} 
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {calculateProgress()}% Complete
                </Typography>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box className="mobile-scroll-area" sx={{ height: '100%', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper className="mobile-sticky-top" sx={{ p: 2, mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <FormIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" className="mobile-title">
              Vehicle Inspection
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {steps.length} • {calculateProgress()}% Complete
            </Typography>
          </Box>
          <Chip 
            label={steps[activeStep].label} 
            color="primary" 
            variant="outlined"
            size="small"
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={(activeStep / (steps.length - 1)) * 100} 
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Paper>

      {/* Content */}
      <Box sx={{ px: 2, pb: 20 }}>
        <Card className="mobile-card">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 40,
                  height: 40,
                  mr: 2
                }}
              >
                {steps[activeStep].icon}
              </Avatar>
              <Typography variant="h6" className="mobile-subtitle">
                {steps[activeStep].label}
              </Typography>
            </Box>

            {renderStepContent(activeStep)}
          </CardContent>
        </Card>

        {submitMessage && (
          <Alert severity={submitMessage.type} sx={{ mx: 0, mt: 2 }}>
            {submitMessage.text}
          </Alert>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Paper 
        sx={{ 
          position: 'fixed',
          bottom: 80, // Position above the mobile navigation (70px + 10px gap)
          left: 0,
          right: 0,
          p: 2, 
          display: 'flex', 
          gap: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1000,
          background: 'white'
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          variant="outlined"
          startIcon={<PrevIcon />}
          className="mobile-button"
          sx={{ flex: 1 }}
        >
          Previous
        </Button>
        
        {activeStep === steps.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant="contained"
            endIcon={<CheckIcon />}
            className="mobile-button"
            sx={{ flex: 2 }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Checklist'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<NextIcon />}
            className="mobile-button"
            sx={{ flex: 1 }}
          >
            Next
          </Button>
        )}
      </Paper>


    </Box>
  );
};

export default MobileChecklistForm; 